import { useEffect } from 'react';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import { refreshCustomerAccessToken } from '@/lib/customer-token-refresh';

const REFRESH_BEFORE_EXPIRY_MS = 2 * 60 * 1000; // 2 minutes

function parseJwtExp(token: string): number | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    const json = atob(segment.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as Record<string, unknown>;
    return typeof payload['exp'] === 'number' ? payload['exp'] : null;
  } catch {
    return null;
  }
}

/**
 * Schedules a silent token refresh 2 minutes before the customer access
 * token expires. Mirrors use-platform-auto-refresh.ts exactly, against the
 * customer auth store instead.
 */
export function useCustomerAutoRefresh(): void {
  const accessToken = useCustomerAuthStore((s) => s.accessToken);
  const isAuthenticated = useCustomerAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    const exp = parseJwtExp(accessToken);
    if (exp === null) return;

    const msUntilExpiry = exp * 1000 - Date.now();
    const msUntilRefresh = msUntilExpiry - REFRESH_BEFORE_EXPIRY_MS;

    if (msUntilRefresh <= 0) {
      void refreshCustomerAccessToken().catch(() => {
        // clearAuth() already called inside refreshCustomerAccessToken on failure
      });
      return;
    }

    const timer = setTimeout(() => {
      void refreshCustomerAccessToken().catch(() => {
        // clearAuth() already called inside refreshCustomerAccessToken on failure
      });
    }, msUntilRefresh);

    return () => clearTimeout(timer);
  }, [accessToken, isAuthenticated]);
}
